import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, BUSINESS, DEFAULTS, absUrl } from "./site";

/**
 * Per-route <head>. Every page must render exactly one <Seo>.
 *
 * The prerender step (scripts/prerender.js) snapshots the DOM after React has
 * run, so whatever this writes into <head> is what Googlebot, Bingbot and the
 * AI crawlers see in the raw HTML — no JS execution required on their side.
 *
 * @param {string}  title        Full <title>. Aim for <= 60 characters.
 * @param {string}  description  Meta description. Aim for <= 155 characters.
 * @param {string}  path         Route path, e.g. "/catalogue/kraft-packaging-boards".
 * @param {string}  [image]      Absolute or root-relative OG image.
 * @param {string}  [imageAlt]
 * @param {boolean} [noindex]    Set on thin/utility pages only.
 * @param {Array}   [schema]     Array of JSON-LD objects to emit for this page.
 * @param {string}  [type]       OpenGraph type. "website" by default.
 */
export default function Seo({
  title,
  description = DEFAULTS.description,
  path = "/",
  image = DEFAULTS.image,
  imageAlt = DEFAULTS.imageAlt,
  noindex = false,
  schema = [],
  type = "website",
}) {
  const canonical = absUrl(path);
  const ogImage = absUrl(image);
  const graph = Array.isArray(schema) ? schema.filter(Boolean) : [schema];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-IN" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex,follow" />
      ) : (
        <meta
          name="robots"
          content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
        />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={BUSINESS.name} />
      <meta property="og:locale" content={DEFAULTS.locale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter / X */}
      <meta name="twitter:card" content={DEFAULTS.twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* Geo / local signals */}
      <meta name="geo.region" content="IN-DL" />
      <meta name="geo.placename" content="New Delhi" />
      <meta
        name="geo.position"
        content={`${BUSINESS.geo.lat};${BUSINESS.geo.lng}`}
      />
      <meta name="ICBM" content={`${BUSINESS.geo.lat}, ${BUSINESS.geo.lng}`} />

      {/* Structured data. One script per node keeps validation errors isolated. */}
      {graph.map((node, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(node)}
        </script>
      ))}

      <link rel="alternate" hrefLang="en-in" href={canonical} />
      <link rel="alternate" hrefLang="x-default" href={canonical} />
      <meta name="author" content={BUSINESS.name} />
      <meta name="publisher" content={BUSINESS.name} />
      <link rel="sitemap" type="application/xml" href={`${SITE_URL}/sitemap.xml`} />
    </Helmet>
  );
}
